import { attr, belongsTo } from "@ember-data/model";
import dayjs from "dayjs";
import {
  validateDate,
  validateInclusion,
  validatePresence
} from "ember-changeset-validations/validators";
import type IntlService from "ember-intl/services/intl";
import JournalEvent from "parog-web/models/journal-event";
import type Patient from "parog-web/models/patient";
import tWithDefault from "parog-web/utils/t-with-default";

//---- Automatically generated, don't update this part ----

type PlannedReplacementTherapyStringEnum =
  | "hemodialysis-in-hospital-center"
  | "hemodialysis-in-a-medicalized-dialysis-unit"
  | "hemodialysis-in-a-self-dialysis-unit"
  | "hemodialysis-at-home"
  | "continuous-ambulatory-peritoneal-dialysis"
  | "automated-peritoneal-dialysis"
  | "conservative-treatment"
  | "not-yet-defined";

type SmokingStringEnum =
  | "active-smoker"
  | "non-smoker"
  | "former-smoker"
  | "unknown";

type SmokingCessationStringEnum = "not-planned" | "planned" | "in-progress";

type ChosenHemodialysisTypeStringEnum =
  | "hemodialysis-in-hospital-center"
  | "hemodialysis-in-a-medicalized-dialysis-unit"
  | "hemodialysis-in-a-self-dialysis-unit"
  | "hemodialysis-at-home";

type PlannedVascularAccessStringEnum =
  | "arteriovenous-fistula"
  | "central-vein-catheter-tunneled-catheter"
  | "graft"
  | "unknown";

type TypeOfPeritonealDialysisStringEnum =
  | "continuous-ambulatory-peritoneal-dialysis"
  | "automated-peritoneal-dialysis";

export default class MrcReplacementJournalEvent extends JournalEvent {
  static type = "mrc-replacement-journal-event";

  @attr("string")
  declare plannedReplacementTherapy: PlannedReplacementTherapyStringEnum;

  @attr("date") declare plannedStartDate: Date;

  @attr("boolean", {
    allowNull: true
  })
  declare patientInformationMeetingHeld: boolean;

  @attr("date") declare meetingDate: Date;

  @attr("string") declare patientLifestyle: string;

  @attr("string") declare smoking: SmokingStringEnum;

  @attr("string") declare smokingCessation: SmokingCessationStringEnum;

  @attr("string")
  declare chosenHemodialysisType: ChosenHemodialysisTypeStringEnum;

  @attr("date") declare dialysisStartDateHaemodialysis: Date;

  @attr("string")
  declare plannedVascularAccess: PlannedVascularAccessStringEnum;

  @attr("date") declare vascularAccessCreationDate: Date;

  @attr("boolean", {
    allowNull: true
  })
  declare functionalVascularAccess: boolean;

  @attr("string") declare plannedDialysisCenter: string;

  @attr("string") declare dialysisSoftwareUsed: string;

  @attr("string")
  declare typeOfPeritonealDialysis: TypeOfPeritonealDialysisStringEnum;

  @attr("date") declare dialysisStartDatePeritoneal: Date;

  @attr("boolean", {
    allowNull: true
  })
  declare meetingWithSurgeon: boolean;

  @attr("date") declare peritonealCatheterPlacementDate: Date;

  static validations(intl: IntlService) {
    return {
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.mrc-replacement-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date",
          onOrBefore: dayjs().endOf("day").toDate()
        })
      ],
      plannedReplacementTherapy: validateInclusion({
        list: [
          "hemodialysis-in-hospital-center",
          "hemodialysis-in-a-medicalized-dialysis-unit",
          "hemodialysis-in-a-self-dialysis-unit",
          "hemodialysis-at-home",
          "continuous-ambulatory-peritoneal-dialysis",
          "automated-peritoneal-dialysis",
          "conservative-treatment",
          "not-yet-defined"
        ],
        allowBlank: true,
        description: intl.t(
          "validations.mrc-replacement-journal-event.planned-replacement-therapy"
        )
      }),
      smoking: validateInclusion({
        list: ["active-smoker", "non-smoker", "former-smoker", "unknown"],
        allowBlank: true,
        description: intl.t("validations.mrc-replacement-journal-event.smoking")
      }),
      smokingCessation: validateInclusion({
        list: ["not-planned", "planned", "in-progress"],
        allowBlank: true,
        description: intl.t(
          "validations.mrc-replacement-journal-event.smoking-cessation"
        )
      }),
      chosenHemodialysisType: validateInclusion({
        list: [
          "hemodialysis-in-hospital-center",
          "hemodialysis-in-a-medicalized-dialysis-unit",
          "hemodialysis-in-a-self-dialysis-unit",
          "hemodialysis-at-home"
        ],
        allowBlank: true,
        description: intl.t(
          "validations.mrc-replacement-journal-event.chosen-hemodialysis-type"
        )
      }),
      plannedVascularAccess: validateInclusion({
        list: [
          "arteriovenous-fistula",
          "central-vein-catheter-tunneled-catheter",
          "graft",
          "unknown"
        ],
        allowBlank: true,
        description: intl.t(
          "validations.mrc-replacement-journal-event.planned-vascular-access"
        )
      }),
      typeOfPeritonealDialysis: validateInclusion({
        list: [
          "continuous-ambulatory-peritoneal-dialysis",
          "automated-peritoneal-dialysis"
        ],
        allowBlank: true,
        description: intl.t(
          "validations.mrc-replacement-journal-event.type-of-peritoneal-dialysis"
        )
      })
    };
  }

  //---- End of generated code ----

  @belongsTo("patient", {
    async: false,
    inverse: "mrcReplacementJournalEvent"
  })
  declare patient: Patient;
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "mrc-replacement-journal-event": MrcReplacementJournalEvent;
  }
}
